import {useDispatch} from 'react-redux';

import {AppButton} from '@/components/Button';
import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import RemoteImage from '@/components/Image/RemoteImage';
import GuideVideo from '@/components/Video/GuideVideo/GuideVideo';
import {VIDEO_PLAY_STATES} from '@/consts/video/videoPlayStates';
import {getDeviceNameByType} from '@/services/device/deviceData';
import {showConnectionCancellationModalAction} from '@/state/slices/modalSlice';
import {formatString} from '@/utils/stringUtils';
import {twx} from '@/utils/tailwindUtils';

const ConnectionGuideContent = () => null;

const Media = ({className, images, videos, bgColorHex}) => {
    return (
        <div
            className={twx('relative flex items-center justify-center', className)}
            style={{backgroundColor: bgColorHex ? `#${bgColorHex}` : 'white'}}
        >
            {images?.length > 0 && <Image imageMedia={images} />}
            {videos?.length > 0 && <Video video={videos[0]} />}
        </div>
    );
};

const Image = ({className, imageMedia}) => {
    return <RemoteImage imageMedia={imageMedia} className={twx('h-[220px] w-[220px]', className)} />;
};

const Video = ({className, video}) => {
    const {path, thumbnailPath, startTime, endTime, isShowPlayPauseButton, showPlayPauseButtonAlt} = video;

    return (
        <GuideVideo
            src={path}
            thumbnailSrc={thumbnailPath}
            playState={VIDEO_PLAY_STATES.PLAYING}
            content={[{videoStartTime: startTime, videoStopTime: endTime}]}
            containerClassName='absolute top-0 left-0 w-full h-full'
            className={twx('h-full w-full', className)}
            controlAriaLabel={showPlayPauseButtonAlt}
            isControlsVisible={isShowPlayPauseButton}
        />
    );
};

const Buttons = ({className, btnClassName, actionButton, closeButton, onNextStep}) => {
    return (
        <div className={twx('flex w-full flex-col gap-4', className)}>
            {actionButton && (
                <AppButton
                    className={twx('mt-0', btnClassName)}
                    primary
                    light
                    aria-label={actionButton.alt}
                    onClick={onNextStep}
                >
                    {actionButton.text}
                </AppButton>
            )}
            {closeButton && <CloseButton alt={closeButton.alt} text={closeButton.text} className={btnClassName} />}
        </div>
    );
};

const CloseButton = ({className, alt, text}) => {
    const dispatch = useDispatch();

    const handleClick = () => {
        dispatch(showConnectionCancellationModalAction());
    };

    return (
        <AppButton className={twx('mt-0', className)} secondary light aria-label={alt} onClick={handleClick}>
            {text}
        </AppButton>
    );
};

const Title = ({className, title, deviceType}) => {
    return (
        <h1 className={twx('mb-4 text-28 leading-143', className)}>
            {formatString(title, getDeviceNameByType(deviceType))}
        </h1>
    );
};

const Description = ({className, description, deviceType}) => {
    if (!description) return null;

    return <p className={className}>{formatString(description, getDeviceNameByType(deviceType))}</p>;
};

const Instruction = ({className, items, deviceType}) => {
    if (!items?.length) return null;
    const itemsSorted = items.sort((a, b) => a.order - b.order);

    return (
        <div className={twx('flex w-full flex-col items-start gap-6', className)}>
            {itemsSorted.map(({title, path}, i) => (
                <InstructionStep key={i} title={title} path={path} deviceType={deviceType} />
            ))}
        </div>
    );
};

const InstructionStep = ({title, path, deviceType}) => {
    return (
        <div className='flex items-start justify-start gap-4 text-start'>
            <RemoteIconV2 icon={{path}} className='h-6 w-6 shrink-0' />
            <p className='flex-1'>{formatString(title, getDeviceNameByType(deviceType))}</p>
        </div>
    );
};

ConnectionGuideContent.Media = Media;
ConnectionGuideContent.Title = Title;
ConnectionGuideContent.Description = Description;
ConnectionGuideContent.Instruction = Instruction;
ConnectionGuideContent.Buttons = Buttons;

export default ConnectionGuideContent;
